// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { Box, Button, Grid2, IconButton, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TFunction } from 'i18next';
// Components, Layouts, Pages
import {
  BaseDatePicker,
  BaseDynamicSelect,
  BaseLabel,
  BaseTable,
  Loading,
  UnderDevelopment,
} from '~/components/common';
// Others
import { IInventoryUsageReport, IInventoryUsageRevenue, IQueryParamListExport } from '~/utils/interface/reports';
import {
  BACK_TO_PREVIOUS_PAGE,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA,
  EMPTY_VALUE,
} from '~/utils/constants/common';
import {
  IBaseOption,
  IHeaderCellType,
  IPayloadCallApiFn,
  IQueryParamBase,
  IResultCallApiFn,
} from '~/utils/interface/common';
import { formatCurrencyUSD } from '~/utils/helper/common';
import { getListCategory } from '~/thunks/category/categoryThunk';
import { ICategory } from '~/utils/interface/category';
import { useAppDispatch } from '~/utils/redux/hooks';
import { getListInventoryUsageReport } from '~/thunks/reports/reportsThunk';
// Styles, images, icons
import styles from './ReportsInventoryUsage.module.scss';
import { icons } from '~/assets';

const cx = classNames.bind(styles);

const columns = (t: TFunction, indexColumn: number): IHeaderCellType<IInventoryUsageRevenue>[] => {
  return [
    {
      id: 'name',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_product_name')}</Box>
        </Stack>
      ),
      render: (record: IInventoryUsageRevenue) => (
        <Stack direction={'column'} spacing={'2px'}>
          <Typography className={cx('titleName')}>{record?.name || EMPTY_VALUE}</Typography>
          <Typography className={cx('variantName')}>{record?.variantName}</Typography>
        </Stack>
      ),
    },
    {
      id: 'category',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_category')}</Box>
        </Stack>
      ),
      render: (record: IInventoryUsageRevenue) => {
        return record?.category?.name || EMPTY_VALUE;
      },
    },
    {
      id: 'quantity',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_quantity_rental')}</Box>
        </Stack>
      ),
      render: (record: IInventoryUsageRevenue) => record?.quantity || EMPTY_VALUE,
    },
    {
      id: 'rentalPrice',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_revenue')}</Box>
        </Stack>
      ),
      render: (record: IInventoryUsageRevenue) => (
        <Typography className={cx('textSubrental')}>
          {formatCurrencyUSD({ value: record?.rentalPrice, showDecimal: true })}
        </Typography>
      ),
    },
    ...(indexColumn === 1
      ? [
          {
            id: 'quantityComparison',
            label: (
              <Stack direction={'row'} alignItems={'center'} gap={2}>
                <Box>{t('report_table_quantity_comparison')}</Box>
              </Stack>
            ),
            render: (record: IInventoryUsageRevenue) => record?.quantityComparison || EMPTY_VALUE,
          },
          {
            id: 'rentalPriceComparison',
            label: (
              <Stack direction={'row'} alignItems={'center'} gap={2}>
                <Box>{t('report_table_revenue_comparison')}</Box>
              </Stack>
            ),
            render: (record: IInventoryUsageRevenue) => (
              <Typography className={cx('textSubrental')}>
                {formatCurrencyUSD({ value: record?.rentalPriceComparison, showDecimal: true })}
              </Typography>
            ),
          },
        ]
      : []),
  ];
};

const ReportsInventoryUsage = () => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/reports', 'common/base']);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [inventoryUsageList, setInventoryUsageList] = useState<IInventoryUsageReport[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [paramsObject, setParamsObject] = useState<IQueryParamListExport>({});
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListOverbookingItem();
  }, [paramsObject.page]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListOverbookingItem = () => {
    setIsLoading(true);

    dispatch(getListInventoryUsageReport(paramsObject))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const { data } = res;

        setInventoryUsageList(convertDataInventoryUsage(data));
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const handleApplyFilter = () => {
    handleGetListOverbookingItem();
  };

  const handleExport = () => {
    // setIsLoading(true);

    // const params: IQueryParamListExport = {
    //   ...paramsObject,
    //   type: '',
    // };

    // dispatch(exportDataReport(params))
    //   .unwrap()
    //   .then((res) => {
    //     const { data } = res;

    //     handleDownloadFile(data?.url);
    //   })
    //   .catch(() => {})
    //   .finally(() => setIsLoading(false));
    setIsShowUnderDevelopment(true);
  };

  const handleDownloadFile = (fileUrl: string) => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileUrl;
    link.click();
  };

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  const handleBack = () => {
    navigate(BACK_TO_PREVIOUS_PAGE);
  };

  const fetchOptionsCategory = useCallback(
    async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IBaseOption>> => {
      const { page, searchKey } = payload;
      const payloadParams: IQueryParamBase = {
        page: page,
        limit: DEFAULT_LIMIT_PAGE,
        ...(searchKey && { textSearch: searchKey }),
      };

      try {
        const resp = await dispatch(getListCategory(payloadParams)).unwrap();

        const { responses: listCategory, pagination } = resp?.data;

        const formatOptions: IBaseOption[] = handleConvertOptionCategory(listCategory);

        const hasMore: boolean = pagination?.page < pagination?.totalPages || false;

        return { optionsResp: formatOptions, hasMoreResp: hasMore };
      } catch (error) {
        return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
      }
    },
    []
  );

  const handleConvertOptionCategory = (data: ICategory[]): IBaseOption[] => {
    const options: IBaseOption[] = data.map((item) => ({
      value: item.id,
      label: item.name,
    }));

    return options;
  };

  const groupByProduct = (items: IInventoryUsageRevenue[]) => {
    const map = new Map<string, IInventoryUsageRevenue>();

    items.forEach((item) => {
      const key = `${item.productId}-${item.variantId}`;

      if (!map.has(key)) {
        map.set(key, { ...item });
      } else {
        const existing = map.get(key)!;

        existing.quantity += item.quantity;
        existing.rentalPrice += item.rentalPrice;
      }
    });

    return [...map.values()];
  };

  const convertDataInventoryUsage = (data: IInventoryUsageReport[]) => {
    if (!Array.isArray(data) || data.length === 0) return [];

    const currentYear = data[data.length - 1]?.year;
    if (currentYear === undefined) return [];

    const currentYearObj = data.find((item) => item.year === currentYear);
    const currentYearRevenue = groupByProduct(currentYearObj?.revenue ?? []);

    const previousYears = data.filter((item) => item.year !== currentYear).map((item) => Number(item.year));

    const previousYearsRevenueRaw = data
      .filter((item) => item.year !== currentYear)
      .flatMap((item) => item.revenue ?? []);

    const previousYearsRevenue = groupByProduct(previousYearsRevenueRaw);

    let previousYearLabel = 'Previous';
    if (previousYears.length > 0) {
      const minYear = Math.min(...previousYears);
      const maxYear = Math.max(...previousYears);
      previousYearLabel = minYear === maxYear ? `${minYear}` : `${minYear} - ${maxYear}`;
    }

    return [
      {
        year: previousYearLabel,
        revenue: previousYearsRevenue,
      },
      {
        year: currentYear,
        revenue: currentYearRevenue,
      },
    ];
  };
  //#endregion Handle Function

  return (
    <Stack id='reportsOverbookingItemPage' className={cx('container')} spacing={6}>
      <Stack direction='row' spacing={2} alignItems='center'>
        <IconButton color='primary' onClick={handleBack} className={cx('buttonBack')}>
          <img src={icons.commonIconBack} alt={t('common_alt_img', { ns: 'common/base' })} />
        </IconButton>

        <Stack direction={'column'} spacing={2}>
          <BaseLabel label={t('reports_inventory_usage_title')} styleType='bigTitle' />
          <Typography className={cx('description')}>{t('reports_inventory_usage_desc_page')}</Typography>
        </Stack>
      </Stack>

      <Stack direction={'column'} gap={5} className={cx('viewAction')}>
        <Grid2 columnSpacing={'24px'} alignItems={'end'} rowSpacing={'24px'} container>
          <Grid2 size={3}>
            <BaseDatePicker
              mode='date'
              height={40}
              label={t('common_text_start_date', { ns: 'common/base' })}
              value={paramsObject.startDate}
              onChange={(date) => {
                setParamsObject((prevState) => ({
                  ...prevState,
                  startDate: date as string,
                }));
              }}
            />
          </Grid2>

          <Grid2 size={3}>
            <BaseDatePicker
              mode='date'
              height={40}
              label={t('common_text_end_date', { ns: 'common/base' })}
              value={paramsObject.endDate}
              onChange={(date) => {
                setParamsObject((prevState) => ({
                  ...prevState,
                  endDate: date as string,
                }));
              }}
            />
          </Grid2>
        </Grid2>

        <BaseDynamicSelect
          multiple={true}
          isSearch={true}
          width={'49%'}
          label={t('report_table_category')}
          callApiFn={fetchOptionsCategory}
          onChange={(data) => {
            setParamsObject((prevState) => ({
              ...prevState,
              categoryId: data?.idSelected,
            }));
          }}
        />

        <Button
          type='button'
          variant='contained'
          className={cx('buttonApply')}
          disabled={!paramsObject.startDate && !paramsObject.endDate && !paramsObject.categoryId}
          onClick={handleApplyFilter}
          startIcon={<img src={icons.commonIconFilter} alt={t('common_alt_img', { ns: 'common/base' })} />}
        >
          {t('common_action_apply', { ns: 'common/base' })}
        </Button>
        {/* <Button
          type='button'
          variant='contained'
          className={cx('buttonExport')}
          onClick={handleExport}
          startIcon={<img src={icons.commonIconExportExcel} alt={t('common_alt_img', { ns: 'common/base' })} />}
        >
          {t('common_button_export_excel', { ns: 'common/base' })}
        </Button> */}
      </Stack>

      <Box className={cx('tableContainer')}>
        {inventoryUsageList?.map((item: IInventoryUsageReport, index: number) => (
          <Stack key={index} direction={'column'} spacing={4}>
            <Typography className={cx('titleYear')}>
              {t('reports_inventory_usage_title_year', { year: item?.year })}
            </Typography>

            <BaseTable dataSource={item?.revenue} columns={columns(t, index)} />
          </Stack>
        ))}
      </Box>

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}

      {isLoading && <Loading />}
    </Stack>
  );
};

export default ReportsInventoryUsage;
